/**
 * /ebook/100-year-retirement-plan
 *
 * Teaser landing page for the Centennial-authored ebook.
 * The manuscript is still being written — this page collects a waitlist
 * and funnels warm traffic into /learn while it's in production.
 *
 * When the book ships, the "Coming Soon / Waitlist" block flips to a
 * Stripe checkout tile (same layout) and the free preview stays.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Loader2,
  Mail,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { joinEbookWaitlist, fetchWaitlistCount } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { errorMessage } from "@/lib/errorMessage";
import useSEO from "@/lib/useSEO";

const CHAPTERS = [
  { n: "01", title: "Why 30-year rules break at 100" },
  { n: "02", title: "Sequence-of-returns risk in plain English" },
  { n: "03", title: "The three-bucket withdrawal order (Taxable → 401k → Roth)" },
  { n: "04", title: "Roth conversion ladders without the spreadsheet" },
  { n: "05", title: "Social Security timing decisions that survive stress tests" },
  { n: "06", title: "Healthcare inflation and the 65-to-75 gap" },
  { n: "07", title: "Glide paths: when to stop being aggressive" },
  { n: "08", title: "Historical stress tests — 1929, 1973, 2000, 2008" },
  { n: "09", title: "Building your own 100-year plan from scratch" },
];

const PREVIEW = `Most retirement math assumes a 30-year horizon. That number comes from
William Bengen's 1994 study — a study that ended in 1995 and looked backward at
retirees who were done by their late 80s. For anyone reading this, the horizon
is different. If you retire at 65, live to 100 (which is not a stretch — one of
every three 65-year-olds today will), you are planning for 35 years of markets
you have never seen. And the sequence in which those markets show up matters
more than their average return.

That is what this book is about: not the math of retirement, but the shape of
it — the shape of a 100-year life, priced in dollars.`;

export default function Ebook100YearRetirementPlan() {
  useSEO({
    title: "The 100-Year Retirement Plan · Centennial ebook · Coming soon",
    description:
      "The playbook behind Centennial's free 100-year retirement calculator. Sequence-of-returns risk, tax-aware withdrawals, Roth ladders, and stress tests. Free preview + waitlist.",
    canonicalPath: "/ebook/100-year-retirement-plan",
  });

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [count, setCount] = useState(null);

  useEffect(() => {
    fetchWaitlistCount()
      .then((d) => setCount(d.count))
      .catch(() => setCount(null));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const r = await joinEbookWaitlist(email.trim(), "ebook_page");
      setCount(r.count);
      setJoined(true);
      track(EVENTS.EBOOK_WAITLIST_JOIN, { source: "ebook_page" });
      toast.success("You're on the list. We'll email you the moment it ships.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not join the waitlist."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link
          to="/books"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-8"
        >
          <ArrowLeft size={14} /> Back to books
        </Link>

        {/* Hero */}
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-3 inline-flex items-center gap-2">
          <Sparkles size={12} /> Centennial original
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C2B23] leading-[1.05]">
          The 100-Year<br />Retirement Plan
        </h1>
        <p className="mt-5 text-lg text-[#5C6B62] leading-relaxed max-w-2xl">
          The playbook behind our free calculator — written by the same people who built it.
          Planning for 40+ years of retirement, in plain English, with worked examples you can
          run yourself.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span
            data-testid="ebook-status-pill"
            className="inline-flex items-center gap-1.5 bg-[#E8F0EB] text-[#2B5339] text-xs px-3 py-1.5 rounded-full font-medium"
          >
            <BookOpen size={12} /> Coming soon
          </span>
          {count !== null && count > 0 && (
            <span data-testid="ebook-waitlist-count" className="text-xs text-[#5C6B62]">
              {count.toLocaleString()} on the waitlist
            </span>
          )}
        </div>

        {/* Waitlist */}
        <section
          data-testid="ebook-waitlist-section"
          className="mt-10 bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
        >
          <div className="grid md:grid-cols-5 gap-6 items-start">
            <div className="md:col-span-3">
              <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2 inline-flex items-center gap-1.5">
                <Mail size={11} /> Waitlist
              </div>
              <h2 className="text-2xl font-medium text-[#1C2B23]">
                Get notified the day it drops.
              </h2>
              <p className="text-sm text-[#5C6B62] mt-2 leading-relaxed">
                One email when the book ships. No spam. No drip campaign. Early readers get a
                launch-week discount and a signed digital copy from the author (that&apos;s us).
              </p>
            </div>
            <div className="md:col-span-2">
              {joined ? (
                <div
                  data-testid="ebook-waitlist-success"
                  className="bg-[#E8F0EB] border border-[#2B5339] rounded-xl p-5 text-sm text-[#1C2B23] flex items-start gap-2.5"
                >
                  <CheckCircle2 size={16} className="text-[#2B5339] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-medium">You&apos;re in.</div>
                    <div className="text-[#5C6B62] mt-1 text-xs">
                      We&apos;ll email you the moment the book is live.
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <input
                    data-testid="ebook-waitlist-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-[#F9F8F6] border border-[#E5E2DC] rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#2B5339]"
                  />
                  <button
                    data-testid="ebook-waitlist-submit"
                    type="submit"
                    disabled={submitting || !email}
                    className="w-full bg-[#1C2B23] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#2B5339] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    Join the waitlist
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Free preview */}
        <section className="mt-12" data-testid="ebook-preview-section">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C6B62] mb-3">
            Free preview · from the introduction
          </div>
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
            <p className="text-[#1C2B23] leading-relaxed whitespace-pre-line">
              {PREVIEW}
            </p>
            <div className="mt-6 pt-6 border-t border-[#E5E2DC] flex flex-wrap items-center gap-3">
              <Link
                to="/learn"
                data-testid="ebook-cta-learn"
                className="inline-flex items-center gap-1.5 bg-[#2B5339] text-white text-sm font-medium rounded-full px-4 py-2 hover:bg-[#1C2B23] transition-colors"
              >
                Read our free lessons while you wait
                <ArrowRight size={13} />
              </Link>
              <Link
                to="/"
                data-testid="ebook-cta-calculator"
                className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339]"
              >
                Or run your own 100-year plan →
              </Link>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section className="mt-12" data-testid="ebook-chapters-section">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#5C6B62] mb-4">
            Table of contents
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CHAPTERS.map((c) => (
              <div
                key={c.n}
                className="flex gap-3 bg-white border border-[#E5E2DC] rounded-xl p-4"
              >
                <div className="font-mono-num text-xs text-[#2B5339] font-semibold pt-0.5">
                  {c.n}
                </div>
                <div className="text-sm text-[#1C2B23] leading-snug">{c.title}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Author + policy */}
        <section className="mt-12 grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
              About the author
            </div>
            <p className="text-sm text-[#1C2B23] leading-relaxed">
              Centennial is the team behind the free 100-year retirement calculator at
              mycentennialplan.com. We built the engine, ran the stress tests, and now we&apos;re
              writing the book that shows anyone how to plan for a 100-year life.
            </p>
          </div>
          <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
              What you get on launch day
            </div>
            <ul className="text-sm text-[#1C2B23] leading-relaxed space-y-2 list-disc pl-4">
              <li>DRM-free PDF + EPUB, instant download</li>
              <li>Worked examples you can copy into the calculator</li>
              <li>Free lifetime updates when tax rules change</li>
              <li>Launch-week discount for waitlist members</li>
            </ul>
          </div>
        </section>

        <p className="text-[11px] text-[#5C6B62] mt-10 italic">
          Centennial is not a licensed financial advisor. This book is educational, not
          personalized financial advice.
        </p>
      </div>
    </div>
  );
}
